import { Check, CircleAlert, Keyboard, Undo2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";

import { listWorkspaceSheets } from "@/api/client";
import type { WorkspaceConfig, WorkspaceOp, WorkspaceSheet } from "@/api/client";
import { useCan } from "@/auth/AuthProvider";
import { StepHeader } from "@/flow/StepHeader";
import {
  feetInches,
  liveConfigs,
  lookOf,
  nextToReview,
  stepFrom,
  stepPane,
  toggled,
  unitOf,
} from "@/flow/workspace";
import type { Box, Look } from "@/flow/workspace";
import {
  hasLine,
  lineNear,
  nudgeOps,
  removeOp,
  repeatOps,
  rowAt,
  rowsOf,
  snapX,
  snapY,
} from "@/flow/grid";
import type { GridLine } from "@/flow/grid";
import { useDocument } from "@/hooks/useDocument";
import { useWorkspace } from "@/hooks/useWorkspace";
import { Badge } from "@/ui/Badge";
import { Button } from "@/ui/Button";
import { cn } from "@/ui/cn";
import { Notice } from "@/ui/Notice";
import { Kbd, Spinner } from "@/ui/primitives";
import { SheetCanvas } from "@/viewer/SheetCanvas";
import type { SheetGeometry } from "@/viewer/SheetCanvas";
import { windowAround } from "@/viewer/TypeViewer";
import { PANE_KEYS, PaneLayer, PanesPanel, UNIT_KEYS, UnitsPanel } from "@/screens/WorkspaceParts";
import type { Mode } from "@/screens/WorkspaceParts";
import { GRID_KEYS, GridLines, GridPanel } from "@/screens/WorkspaceGrid";

const WHOLE: Box = { x0: 0, y0: 0, x1: 1, y1: 1 };

const STROKE: Record<Look, string> = {
  approved: "var(--color-ok)",
  flagged: "var(--color-warn)",
  proposed: "var(--color-accent)",
};

/** Keys of configuration mode, shown in the help panel. */
export const KEYS: readonly [string, string][] = [
  ["J / K", "Next or previous configuration"],
  ["A", "Approve this configuration"],
  ["Enter", "Approve the sheet and go to the next"],
  ["N", "Draw a configuration the detector missed"],
  ["B", "Redraw this configuration's box"],
  ["H", "Split units at the pointer"],
  ["Del", "Delete this configuration"],
  ["E", "Rename it"],
  ["C", "Leave a note"],
  ["I / F", "Focus on it, or fit the sheet"],
  ["P / U", "Its panes, or its units"],
  ["Z", "Undo"],
];

type Drawing = "new" | "inner" | "hole" | "box" | "pane" | "pane-box" | null;
type Editing = "label" | "note" | "tags" | "name" | "typical" | null;

const DRAWING_HINT: Record<Exclude<Drawing, null>, string> = {
  new: "Drag a box around the configuration",
  inner: "Drag an inner border: its own grid",
  hole: "Drag a hole: what it covers is left out",
  box: "Drag its new box",
  pane: "Drag a box around the pane",
  "pane-box": "Drag the pane's new box",
};

function contains(b: Box, x: number, y: number): boolean {
  return b.x0 <= x && x <= b.x1 && b.y0 <= y && y <= b.y1;
}

function area(b: Box): number {
  return (b.x1 - b.x0) * (b.y1 - b.y0);
}

/**
 * The sheet workspace (ADR-0020): one sheet of a run at a time, its configurations
 * drawn on the sheet as the detector proposed them. The estimator moves through them
 * with the keyboard, fixes what is wrong, and approves; every change is a workspace
 * operation, and approving puts the units in the take-off.
 */
export function WorkspaceScreen() {
  const { id = "", runId = "", page } = useParams();
  const navigate = useNavigate();
  const { doc } = useDocument(id);
  const canEdit = useCan("review:write");
  const [sheets, setSheets] = useState<WorkspaceSheet[] | null>(null);
  const [sheetsError, setSheetsError] = useState<string | null>(null);
  const pageIndex = page === undefined ? (sheets?.[0]?.page_index ?? null) : Number(page);
  const { ws, loaded, error, busy, run, undo, canUndo } = useWorkspace(runId, pageIndex);
  const [selected, setSelected] = useState<string | null>(null);
  const [view, setView] = useState<Box>(WHOLE);
  const [drawing, setDrawing] = useState<Drawing>(null);
  const [editing, setEditing] = useState<Editing>(null);
  const [mode, setMode] = useState<Mode>("configs");
  const [pane, setPane] = useState<string | null>(null);
  const [panesChosen, setPanesChosen] = useState<string[]>([]);
  const [line, setLine] = useState<GridLine | null>(null);
  const [typicalRow, setTypicalRow] = useState<number | null>(null);
  const [help, setHelp] = useState(false);
  const pointer = useRef<[number, number] | null>(null);

  const refreshSheets = useCallback(async () => {
    try {
      setSheets(await listWorkspaceSheets(runId));
    } catch {
      setSheetsError("could not list the run's sheets");
    }
  }, [runId]);

  useEffect(() => {
    void refreshSheets();
  }, [refreshSheets]);

  useEffect(() => {
    setSelected(null);
    setView(WHOLE);
    setMode("configs");
  }, [pageIndex]);

  useEffect(() => {
    setPane(null);
    setPanesChosen([]);
    setLine(null);
  }, [selected, mode]);

  useEffect(() => {
    if (ws && (!selected || !ws.configurations.some((c) => c.id === selected))) {
      setSelected(stepFrom(ws, null, 1));
    }
  }, [ws, selected]);

  const live = useMemo(() => (ws ? liveConfigs(ws) : []), [ws]);
  const chosen = ws?.configurations.find((c) => c.id === selected) ?? null;
  const elevation = ws?.kind === "elevation";
  const grid = !!chosen && !chosen.deleted && (chosen.kind === "outer" || chosen.kind === "inner");

  useEffect(() => {
    if (line && (!chosen || !hasLine(chosen, line))) setLine(null);
  }, [chosen, line]);
  const sheetAt = sheets?.findIndex((s) => s.page_index === pageIndex) ?? -1;
  const sheet = sheetAt >= 0 ? sheets![sheetAt]! : null;

  const act = useCallback(
    async (ops: WorkspaceOp[]) => {
      if (!canEdit) return false;
      const ok = await run(ops);
      if (ok) void refreshSheets();
      return ok;
    },
    [canEdit, run, refreshSheets],
  );

  const goSheet = useCallback(
    (index: number) => {
      const target = sheets?.[index];
      if (target) void navigate(`/documents/${id}/runs/${runId}/review/${target.page_index}`);
    },
    [sheets, navigate, id, runId],
  );

  const approveSheet = useCallback(async () => {
    if (!(await act([{ op: "approve_sheet" }])) || !sheets) return;
    const after = sheets.findIndex((s, i) => i > sheetAt && s.approved < s.configurations);
    if (after >= 0) goSheet(after);
    else void navigate(`/documents/${id}/runs/${runId}/takeoff`);
  }, [act, sheets, sheetAt, goSheet, navigate, id, runId]);

  const onDrawn = useCallback(
    (box: Box) => {
      const mode = drawing;
      setDrawing(null);
      if (mode === "new" || mode === "inner" || mode === "hole") {
        const kind = mode === "new" ? (elevation ? "outer" : "view") : mode;
        void act([{ op: "add_config", box, label: mode === "hole" ? "Hole" : "", kind }]);
      } else if (mode === "box" && chosen) {
        void act([{ op: "move_config", configuration_id: chosen.id, box }]);
      } else if (mode === "pane" && chosen) {
        void act([{ op: "add_pane", configuration_id: chosen.id, box, kind: "pane", tags: [] }]);
      } else if (mode === "pane-box" && pane) {
        void act([{ op: "edit_pane", pane_id: pane, box }]);
      }
    },
    [drawing, act, chosen, pane, elevation],
  );

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA")) return;
      if (event.metaKey || event.ctrlKey || event.altKey || !ws) return;
      const key = event.key.length === 1 ? event.key.toLowerCase() : event.key;
      const editKeys = new Set([
        "a",
        "Enter",
        "n",
        "b",
        "h",
        "Delete",
        "Backspace",
        "e",
        "c",
        "z",
        "t",
        "d",
        "v",
        "g",
        "x",
        "m",
        "r",
      ]);
      if (editKeys.has(key) && !canEdit) return;
      const handled = () => event.preventDefault();
      if (key === "Escape" && mode !== "configs" && !drawing && !editing) {
        handled();
        setMode("configs");
        return;
      }
      if (chosen && mode === "panes") {
        const current = chosen.panes.find((p) => p.id === pane) ?? null;
        switch (key) {
          case "Tab":
            handled();
            setPane(stepPane(chosen, pane, event.shiftKey ? -1 : 1));
            return;
          case "n":
            handled();
            setDrawing("pane");
            return;
          case "b":
            if (current) {
              handled();
              setDrawing("pane-box");
            }
            return;
          case "t":
            if (current) {
              handled();
              setEditing("tags");
            }
            return;
          case "d":
          case "v":
            if (current) {
              handled();
              const kind = toggled(current.kind, key === "d" ? "door" : "leaf");
              void act([{ op: "edit_pane", pane_id: current.id, kind }]);
            }
            return;
          case "Delete":
          case "Backspace":
            if (current) {
              handled();
              const next = stepPane(chosen, current.id, 1);
              void act([{ op: "delete_pane", pane_id: current.id }]).then(() =>
                setPane(next === current.id ? null : next),
              );
            }
            return;
          case "p":
            handled();
            setMode("configs");
            return;
          default:
        }
      }
      if (mode === "configs" && elevation) {
        const at = pointer.current;
        const span = view.x1 - view.x0;
        switch (key) {
          case "n":
            if (event.shiftKey) {
              handled();
              setDrawing("inner");
              return;
            }
            break;
          case "x":
            handled();
            setDrawing("hole");
            return;
          case "h":
            if (grid && at && contains(chosen.bbox_norm, at[0], at[1])) {
              handled();
              void act([{ op: "add_break", configuration_id: chosen.id, y: snapY(chosen, at[1]) }]);
            }
            return;
          case "v":
            if (grid && at && contains(chosen.bbox_norm, at[0], at[1])) {
              handled();
              const x = snapX(chosen, at[0]);
              const row = rowsOf(chosen)[rowAt(chosen, at[1])];
              void act([
                event.shiftKey && row
                  ? { op: "add_vline", configuration_id: chosen.id, x, y0: row[0], y1: row[1] }
                  : { op: "add_vline", configuration_id: chosen.id, x, y0: null, y1: null },
              ]);
            }
            return;
          case "t":
            if (grid && at && rowAt(chosen, at[1]) >= 0) {
              handled();
              setTypicalRow(rowAt(chosen, at[1]));
              setEditing("typical");
            }
            return;
          case "r":
            if (grid && line) {
              handled();
              const ops = repeatOps(chosen, line);
              if (ops.length) void act(ops);
            }
            return;
          case "Delete":
          case "Backspace":
            if (grid && line) {
              handled();
              void act([removeOp(chosen, line)]);
              setLine(null);
              return;
            }
            break;
          case "ArrowLeft":
          case "ArrowRight":
          case "ArrowUp":
          case "ArrowDown": {
            if (!grid || !line) break;
            const across = key === "ArrowLeft" || key === "ArrowRight";
            if (across !== (line.kind === "vline")) {
              handled();
              return;
            }
            const sign = key === "ArrowLeft" || key === "ArrowUp" ? -1 : 1;
            const moved = nudgeOps(chosen, line, (sign * span * (event.shiftKey ? 10 : 1)) / 400);
            handled();
            if (moved) {
              void act(moved.ops).then((ok) => ok && setLine(moved.to));
            }
            return;
          }
          case "Escape":
            if (line) {
              handled();
              setLine(null);
              return;
            }
            break;
          default:
        }
      }
      if (chosen && mode === "units") {
        switch (key) {
          case "g":
            if (panesChosen.length) {
              handled();
              void act([{ op: "group_units", configuration_id: chosen.id, pane_ids: panesChosen }]);
            }
            return;
          case "x":
            if (panesChosen.length) {
              handled();
              void act([{ op: "split_unit", configuration_id: chosen.id, pane_ids: panesChosen }]);
            }
            return;
          case "r":
            handled();
            void act([{ op: "reset_units", configuration_id: chosen.id }]);
            return;
          case "m":
            if (panesChosen.length) {
              handled();
              setEditing("name");
            }
            return;
          case "u":
            handled();
            setMode("configs");
            return;
          default:
        }
      }
      switch (key) {
        case "j":
        case "ArrowDown":
          handled();
          setSelected(stepFrom(ws, selected, 1));
          break;
        case "k":
        case "ArrowUp":
          handled();
          setSelected(stepFrom(ws, selected, -1));
          break;
        case "a":
          if (chosen && !chosen.deleted) {
            handled();
            void act([{ op: "approve_config", configuration_id: chosen.id }]).then((ok) => {
              if (ok) setSelected((s) => nextToReview(ws, s) ?? s);
            });
          }
          break;
        case "Enter":
          handled();
          void approveSheet();
          break;
        case "n":
          handled();
          setDrawing("new");
          break;
        case "b":
          if (chosen) {
            handled();
            setDrawing("box");
          }
          break;
        case "h": {
          const at = pointer.current;
          if (chosen && at && contains(chosen.bbox_norm, at[0], at[1])) {
            handled();
            void act([{ op: "add_break", configuration_id: chosen.id, y: at[1] }]);
          }
          break;
        }
        case "Delete":
        case "Backspace":
          if (chosen && !chosen.deleted) {
            handled();
            const next = stepFrom(ws, chosen.id, 1);
            void act([{ op: "delete_config", configuration_id: chosen.id }]).then(() =>
              setSelected(next),
            );
          }
          break;
        case "e":
          handled();
          setEditing("label");
          break;
        case "c":
          handled();
          setEditing("note");
          break;
        case "z":
          handled();
          void undo().then(() => refreshSheets());
          break;
        case "i":
          if (chosen) {
            handled();
            setView(windowAround(chosen.bbox_norm, 0.4));
          }
          break;
        case "f":
          handled();
          setView({ ...WHOLE });
          break;
        case "p":
          if (chosen) {
            handled();
            setMode("panes");
            setView(windowAround(chosen.bbox_norm, 0.4));
          }
          break;
        case "u":
          if (chosen) {
            handled();
            setMode("units");
            setView(windowAround(chosen.bbox_norm, 0.4));
          }
          break;
        case "?":
          setHelp((h) => !h);
          break;
        case "Escape":
          setDrawing(null);
          setEditing(null);
          setHelp(false);
          break;
        default:
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [
    ws,
    selected,
    chosen,
    act,
    approveSheet,
    undo,
    canEdit,
    refreshSheets,
    mode,
    pane,
    panesChosen,
    drawing,
    editing,
    elevation,
    grid,
    line,
    view,
  ]);

  const pick = (point: [number, number], shift: boolean) => {
    if (chosen && mode !== "configs") {
      const hits = chosen.panes.filter((p) => contains(p.bbox_norm, point[0], point[1]));
      const hit = hits.length
        ? hits.reduce((a, b) => (area(a.bbox_norm) <= area(b.bbox_norm) ? a : b))
        : null;
      if (mode === "panes") {
        setPane(hit?.id ?? null);
      } else if (!hit) {
        setPanesChosen([]);
      } else if (shift) {
        setPanesChosen((now) =>
          now.includes(hit.id) ? now.filter((i) => i !== hit.id) : [...now, hit.id],
        );
      } else {
        setPanesChosen([...(unitOf(chosen, hit.id)?.pane_ids ?? [hit.id])]);
      }
      return;
    }
    if (grid && chosen) {
      const near = lineNear(chosen, point, (view.x1 - view.x0) * 0.006);
      if (near) {
        setLine(near);
        return;
      }
    }
    setLine(null);
    const hits = live.filter((c) => contains(c.bbox_norm, point[0], point[1]));
    if (hits.length) {
      setSelected(hits.reduce((a, b) => (area(a.bbox_norm) <= area(b.bbox_norm) ? a : b)).id);
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <StepHeader
        step="review"
        documentId={id}
        filename={doc?.filename}
        runId={runId}
        runFinished
        review
      />
      {sheetsError ? <Notice tone="danger">{sheetsError}</Notice> : null}
      {sheets && sheets.length === 0 ? (
        <main className="mx-auto max-w-lg p-10 text-center">
          <p className="text-ink-2">
            This run found no sheet of configuration views or elevations to review.
          </p>
          <Link className="mt-4 inline-block" to={`/documents/${id}/runs/${runId}/takeoff`}>
            Open the take-off
          </Link>
        </main>
      ) : (
        <div className="grid min-h-0 flex-1 grid-cols-[220px_1fr_320px]">
          <nav aria-label="Sheets" className="overflow-y-auto border-r border-line bg-surface">
            <ol>
              {(sheets ?? []).map((s, i) => {
                const done = s.configurations > 0 && s.approved === s.configurations;
                return (
                  <li key={s.page_index}>
                    <button
                      type="button"
                      onClick={() => goSheet(i)}
                      aria-current={s.page_index === pageIndex ? "page" : undefined}
                      className={cn(
                        "flex w-full items-center gap-2 border-b border-line px-3 py-2 text-left text-sm",
                        s.page_index === pageIndex ? "bg-accent-soft" : "hover:bg-sunken",
                      )}
                    >
                      <span className="min-w-0 flex-1">
                        <span className="block font-medium text-ink">
                          {s.sheet_number ?? `Page ${s.page_index + 1}`}
                        </span>
                        <span className="block text-xs text-muted">
                          {s.approved} of {s.configurations} approved
                        </span>
                      </span>
                      {done ? <Check className="size-4 text-ok" aria-label="Approved" /> : null}
                      {s.flags ? (
                        <Badge tone="warn" title="Things to check">
                          {s.flags}
                        </Badge>
                      ) : null}
                    </button>
                  </li>
                );
              })}
            </ol>
          </nav>

          <section className="relative min-h-0" aria-label="Sheet">
            {sheet && ws ? (
              <SheetCanvas
                src={sheet.l1_url}
                label={`Sheet ${sheet.sheet_number ?? pageIndex}`}
                window={view}
                drawing={drawing !== null}
                onDrawn={onDrawn}
                onPointer={(p) => {
                  pointer.current = p;
                }}
                onClick={(p, shift) => pick(p, shift)}
              >
                {(g) => (
                  <>
                    <Configurations
                      geometry={g}
                      configs={ws.configurations}
                      selected={selected}
                      quiet={mode !== "configs"}
                    />
                    {grid && mode === "configs" ? (
                      <GridLines geometry={g} config={chosen} line={line} />
                    ) : null}
                    {chosen && mode !== "configs" ? (
                      <PaneLayer
                        geometry={g}
                        config={chosen}
                        mode={mode}
                        pane={pane}
                        chosen={panesChosen}
                      />
                    ) : null}
                  </>
                )}
              </SheetCanvas>
            ) : (
              <p className="flex h-full items-center justify-center gap-2 text-muted" role="status">
                {loaded && error ? (
                  error
                ) : (
                  <>
                    <Spinner /> Opening the sheet…
                  </>
                )}
              </p>
            )}
            {drawing ? (
              <p className="absolute top-3 left-1/2 -translate-x-1/2 rounded-md bg-surface px-3 py-1 text-sm shadow-card">
                {DRAWING_HINT[drawing]}
                {" · "}
                <Kbd>Esc</Kbd> to stop
              </p>
            ) : null}
          </section>

          <aside className="flex min-h-0 flex-col overflow-y-auto border-l border-line bg-surface p-4">
            {error && loaded ? <Notice tone="danger">{error}</Notice> : null}
            {chosen ? (
              <div className="space-y-4">
                <div role="tablist" aria-label="What to work on" className="flex gap-1">
                  {(
                    [
                      ["configs", "Configuration", ""],
                      ["panes", "Panes", "P"],
                      ["units", "Units", "U"],
                    ] as const
                  ).map(([m, title, k]) => (
                    <button
                      key={m}
                      type="button"
                      role="tab"
                      aria-selected={mode === m}
                      onClick={() => setMode(m)}
                      className={cn(
                        "rounded-md px-2 py-1 text-[13px]",
                        mode === m
                          ? "bg-accent-soft font-semibold text-accent"
                          : "text-muted hover:bg-sunken",
                      )}
                    >
                      {title} {k ? <Kbd>{k}</Kbd> : null}
                    </button>
                  ))}
                </div>
                {mode === "panes" ? (
                  <PanesPanel
                    config={chosen}
                    pane={pane}
                    editingTags={editing === "tags"}
                    canEdit={canEdit}
                    onPick={setPane}
                    onOps={(ops) => void act(ops)}
                    onDone={() => setEditing(null)}
                  />
                ) : mode === "units" ? (
                  <UnitsPanel
                    config={chosen}
                    chosen={panesChosen}
                    naming={editing === "name"}
                    canEdit={canEdit}
                    onChoose={setPanesChosen}
                    onOps={(ops) => void act(ops)}
                    onDone={() => setEditing(null)}
                  />
                ) : (
                  <>
                    <ConfigPanel
                      config={chosen}
                      editing={editing === "label" || editing === "note" ? editing : null}
                      canEdit={canEdit}
                      onDone={() => setEditing(null)}
                      onOps={(ops) => void act(ops)}
                    />
                    {grid && ws ? (
                      <GridPanel
                        config={chosen}
                        heightPt={ws.height_pt}
                        row={editing === "typical" ? typicalRow : null}
                        canEdit={canEdit}
                        onOps={(ops) => void act(ops)}
                        onDone={() => setEditing(null)}
                      />
                    ) : null}
                  </>
                )}
              </div>
            ) : elevation ? (
              <p className="text-sm text-muted">
                No border was found on this elevation. Press <Kbd>N</Kbd> and drag one around it:
                its panes are read from the drawing.
              </p>
            ) : (
              <p className="text-sm text-muted">No configuration chosen.</p>
            )}
            <div className="mt-auto space-y-2 pt-4">
              {canEdit ? (
                <>
                  <Button
                    className="w-full"
                    variant="primary"
                    onClick={() => void approveSheet()}
                    disabled={busy || !live.length}
                  >
                    Approve sheet <Kbd>Enter</Kbd>
                  </Button>
                  <Button
                    className="w-full"
                    variant="ghost"
                    onClick={() => void undo().then(() => refreshSheets())}
                    disabled={!canUndo || busy}
                  >
                    <Undo2 aria-hidden="true" /> Undo <Kbd>Z</Kbd>
                  </Button>
                </>
              ) : null}
              <Button className="w-full" variant="ghost" onClick={() => setHelp((h) => !h)}>
                <Keyboard aria-hidden="true" /> Keys <Kbd>?</Kbd>
              </Button>
              {help ? (
                <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs" aria-label="Keys">
                  {(mode === "panes"
                    ? PANE_KEYS
                    : mode === "units"
                      ? UNIT_KEYS
                      : elevation
                        ? GRID_KEYS
                        : KEYS
                  ).map(([k, what]) => (
                    <div key={k} className="contents">
                      <dt>
                        <Kbd>{k}</Kbd>
                      </dt>
                      <dd className="text-ink-2">{what}</dd>
                    </div>
                  ))}
                </dl>
              ) : null}
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}

function Configurations({
  geometry,
  configs,
  selected,
  quiet = false,
}: {
  geometry: SheetGeometry;
  configs: readonly WorkspaceConfig[];
  selected: string | null;
  /** Panes or units are on show: only the chosen box, and no unit outlines. */
  quiet?: boolean;
}) {
  const font = geometry.viewWidth / 90;
  return (
    <g>
      {configs.map((c) => {
        const r = geometry.px(c.bbox_norm);
        const look = lookOf(c);
        const isChosen = c.id === selected;
        if (quiet && !isChosen) {
          return null;
        }
        if (c.deleted) {
          return (
            <rect
              key={c.id}
              {...r}
              fill="none"
              stroke="var(--color-muted)"
              strokeDasharray="4 4"
              strokeWidth={1}
              vectorEffect="non-scaling-stroke"
              data-deleted
            />
          );
        }
        const hole = c.kind === "hole";
        const colour = hole ? "var(--color-muted)" : STROKE[look];
        return (
          <g
            key={c.id}
            data-config={c.label || "unnamed"}
            data-kind={c.kind}
            data-look={look}
            data-chosen={isChosen || undefined}
          >
            <rect
              {...r}
              fill={colour}
              fillOpacity={hole ? 0.3 : isChosen ? 0.1 : 0.03}
              stroke={colour}
              strokeDasharray={c.kind === "inner" ? "8 4" : undefined}
              strokeWidth={isChosen ? 3 : 1.5}
              vectorEffect="non-scaling-stroke"
            />
            {isChosen
              ? c.breaks.map((y) => {
                  const line = geometry.px({
                    x0: c.bbox_norm.x0,
                    y0: y,
                    x1: c.bbox_norm.x1,
                    y1: y,
                  });
                  return (
                    <line
                      key={y}
                      x1={line.x}
                      x2={line.x + line.width}
                      y1={line.y}
                      y2={line.y}
                      stroke="var(--color-violet)"
                      strokeWidth={2}
                      vectorEffect="non-scaling-stroke"
                      data-break
                    />
                  );
                })
              : null}
            {isChosen && !quiet
              ? c.units.map((u) => (
                  <rect
                    key={u.key}
                    {...geometry.px(u.bbox_norm)}
                    fill="none"
                    stroke="var(--color-violet)"
                    strokeDasharray="5 3"
                    strokeWidth={1.25}
                    vectorEffect="non-scaling-stroke"
                    data-unit
                  />
                ))
              : null}
            <text x={r.x} y={r.y - font * 0.4} fontSize={font} fill={colour} fontWeight={600}>
              {hole ? "Hole (left out)" : c.label || "Unnamed"}
              {c.kind === "inner" ? " · inner" : ""}
            </text>
          </g>
        );
      })}
    </g>
  );
}

function ConfigPanel({
  config,
  editing,
  canEdit,
  onDone,
  onOps,
}: {
  config: WorkspaceConfig;
  editing: "label" | "note" | null;
  canEdit: boolean;
  onDone: () => void;
  onOps: (ops: WorkspaceOp[]) => void;
}) {
  const [label, setLabel] = useState(config.label);
  const [note, setNote] = useState(config.note);
  useEffect(() => {
    setLabel(config.label);
    setNote(config.note);
  }, [config.id, config.label, config.note]);
  const look = lookOf(config);

  return (
    <div className="space-y-4">
      <header className="space-y-1">
        {editing === "label" && canEdit ? (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              onOps([{ op: "label_config", configuration_id: config.id, label }]);
              onDone();
            }}
          >
            <input
              autoFocus
              aria-label="Configuration name"
              className="w-full rounded-md border border-line-strong bg-surface px-2 py-1 text-lg font-semibold"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              onBlur={onDone}
              onKeyDown={(e) => e.key === "Escape" && onDone()}
            />
          </form>
        ) : (
          <h2 className="text-lg font-semibold text-ink">{config.label || "Unnamed"}</h2>
        )}
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone={look === "approved" ? "ok" : look === "flagged" ? "warn" : "accent"} dot>
            {config.deleted ? "Deleted" : config.status === "approved" ? "Approved" : "Proposed"}
          </Badge>
          {config.scale ? <span className="text-xs text-muted">{config.scale.label}</span> : null}
        </div>
      </header>

      {config.flags.length ? (
        <ul className="space-y-1" aria-label="To check">
          {config.flags.map((f, i) => (
            <li key={i} className="flex items-start gap-1.5 text-sm text-warn">
              <CircleAlert className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
              {f.text}
            </li>
          ))}
        </ul>
      ) : null}

      <section aria-label="Units">
        <h3 className="mb-1 text-xs font-semibold tracking-wide text-muted uppercase">
          Units ({config.units.length})
        </h3>
        <ul className="divide-y divide-line rounded-md border border-line">
          {config.units.map((u) => (
            <li
              key={u.key}
              className="flex items-baseline justify-between gap-2 px-2 py-1.5 text-sm"
            >
              <span className={cn("font-medium", u.edited ? "text-ink" : "text-ink-2")}>
                {u.name}
                {u.qty > 1 ? <span className="text-muted"> ×{u.qty}</span> : null}
              </span>
              <span className="text-xs text-muted">
                {feetInches(u.width_in)} × {feetInches(u.height_in)}
              </span>
            </li>
          ))}
        </ul>
      </section>

      {editing === "note" && canEdit ? (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            onOps([{ op: "note", configuration_id: config.id, text: note }]);
            onDone();
          }}
          className="space-y-2"
        >
          <textarea
            autoFocus
            aria-label="Note"
            className="h-24 w-full rounded-md border border-line-strong bg-surface p-2 text-sm"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            onKeyDown={(e) => e.key === "Escape" && onDone()}
          />
          <Button size="sm" type="submit">
            Save note
          </Button>
        </form>
      ) : config.note ? (
        <p className="rounded-md bg-sunken p-2 text-sm text-ink-2">{config.note}</p>
      ) : null}

      {canEdit ? (
        <div className="flex flex-wrap gap-2">
          {config.deleted ? (
            <Button
              size="sm"
              onClick={() => onOps([{ op: "restore_config", configuration_id: config.id }])}
            >
              Restore
            </Button>
          ) : (
            <>
              <Button
                size="sm"
                variant="primary"
                disabled={config.status === "approved"}
                onClick={() => onOps([{ op: "approve_config", configuration_id: config.id }])}
              >
                Approve <Kbd>A</Kbd>
              </Button>
              <Button
                size="sm"
                onClick={() => onOps([{ op: "delete_config", configuration_id: config.id }])}
              >
                Delete <Kbd>Del</Kbd>
              </Button>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}
